// app/javascript/controllers/order_chat_controller.js
// Handles order messaging UI - send messages, scroll management
// Real-time receiving is handled by Turbo Streams
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messagesList", "input", "form", "emptyState", "files", "pending", "error"]
  static values = {
    orderToken: String,
    currentUserId: Number,
    maxFiles: { type: Number, default: 5 },
    maxFileBytes: { type: Number, default: 10 * 1024 * 1024 },
    tooManyMessage: String,
    tooLargeMessage: String
  }
  connect() {
    this.scrollToBottom()

    // Use MutationObserver to detect when new messages are added
    // This is more reliable than Turbo events for scroll behavior
    if (this.hasMessagesListTarget) {
      this.observer = new MutationObserver((mutations) => {
        // Check if children were added (new messages)
        const hasNewNodes = mutations.some(m => m.addedNodes.length > 0)
        if (hasNewNodes) {
          this.scrollToBottom()
          this.hideEmptyState()
        }
      })
      this.observer.observe(this.messagesListTarget, { childList: true })
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  // Send message via API
  async send(event) {
    event.preventDefault()

    const body = this.inputTarget.value.trim()
    const files = this.selectedFiles()
    if (!body && files.length === 0) return
    this.showError("")

    // Disable form while sending
    this.inputTarget.disabled = true
    const submitBtn = this.formTarget.querySelector('button[type="submit"]')
    if (submitBtn) submitBtn.disabled = true

    try {
      // Files go as multipart; text-only stays JSON.
      let response
      if (files.length > 0) {
        const form = new FormData()
        form.append('body', body)
        files.forEach((f) => form.append('files[]', f))
        response = await fetch(`/api/orders/${this.orderTokenValue}/messages`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': this.csrfToken },
          body: form
        })
      } else {
        response = await fetch(`/api/orders/${this.orderTokenValue}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ body })
        })
      }

      if (response.ok) {
        // Clear input and reset height on success
        this.inputTarget.value = ''
        this.inputTarget.style.height = 'auto'
        this.clearFiles()
        this.hideEmptyState()
      } else {
        const data = await response.json().catch(() => ({}))
        console.error('[Chat] Send failed:', data.errors)
        this.showError((data.errors || []).join('. '))
      }
    } catch (error) {
      console.error('[Chat] Send error:', error)
    } finally {
      // Re-enable form
      this.inputTarget.disabled = false
      if (submitBtn) submitBtn.disabled = false
      this.inputTarget.focus()
    }
  }

  // ---------- files ----------

  pickFiles() {
    if (this.hasFilesTarget) this.filesTarget.click()
  }

  filesChosen() {
    const files = this.selectedFiles()
    if (files.length > this.maxFilesValue) {
      this.showError(this.tooManyMessageValue)
      this.clearFiles()
      return
    }
    const big = files.find((f) => f.size > this.maxFileBytesValue)
    if (big) {
      this.showError(`${big.name}: ${this.tooLargeMessageValue}`)
      this.clearFiles()
      return
    }
    this.showError("")
    this.renderPending(files)
  }

  selectedFiles() {
    return this.hasFilesTarget ? Array.from(this.filesTarget.files || []) : []
  }

  clearFiles() {
    if (this.hasFilesTarget) this.filesTarget.value = ''
    this.renderPending([])
  }

  renderPending(files) {
    if (!this.hasPendingTarget) return
    this.pendingTarget.innerHTML = ''
    this.pendingTarget.classList.toggle('hidden', files.length === 0)
    files.forEach((f) => {
      const chip = document.createElement('span')
      chip.className = 'inline-flex items-center gap-1 px-2 py-1 rounded-full bg-bg-cream text-xs text-text-primary max-w-full'
      const icon = document.createElement('i')
      icon.className = f.type.startsWith('image/') ? 'fas fa-image' : 'fas fa-file'
      const name = document.createElement('span')
      name.className = 'truncate max-w-[12rem]'
      name.textContent = f.name
      chip.append(icon, name)
      this.pendingTarget.appendChild(chip)
    })
  }

  showError(text) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = text
    this.errorTarget.classList.toggle('hidden', !text)
  }

  // Handle Enter key to send (Shift+Enter for newline)
  handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.send(event)
    }
  }

  // Auto-resize textarea as user types
  autoResize(event) {
    const textarea = event.target
    textarea.style.height = 'auto'
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
  }

  scrollToBottom() {
    if (this.hasMessagesListTarget) {
      this.messagesListTarget.scrollTop = this.messagesListTarget.scrollHeight
    }
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('hidden')
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
